import AddIcon from '@mui/icons-material/Add';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import ViewSidebarIcon from '@mui/icons-material/ViewSidebar';
import {
  Box,
  Button,
  Divider,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useMemo } from 'react';
import { STUDIO_PANEL } from './studioDesign';

type ConfigRow = { id: string; name: string };

const PALETTE: Array<{ type: string; label: string }> = [
  { type: 'text', label: 'Text' },
  { type: 'textarea', label: 'Text area' },
  { type: 'number', label: 'Number' },
  { type: 'date', label: 'Date' },
  { type: 'address', label: 'Address' },
  { type: 'currency', label: 'Currency' },
  { type: 'ref_field', label: 'Ref Field' },
  { type: 'single_select', label: 'Dropdown' },
  { type: 'multi_select', label: 'Multi select' },
  { type: 'radio', label: 'Radio' },
  { type: 'checkbox', label: 'Checkbox' },
  { type: 'boolean', label: 'Toggle' },
  { type: 'email', label: 'Email' },
  { type: 'phone', label: 'Phone' },
  { type: 'file', label: 'File' },
  { type: 'link', label: 'Link' },
  { type: 'space', label: 'Space' },
] as const;

export default function SimpleBuilderInspectorPanel(props: {
  activeTenantId: string | null;
  configs: ConfigRow[];
  selectedConfigId: string;
  onSelectConfigId: (id: string) => void;

  formSchema: Record<string, unknown> | null;
  selectedSectionIndex: number;
  onSelectSectionIndex: (idx: number) => void;

  onOpenAddSection: () => void;
  onQuickAddFieldType: (type: string) => void;
}) {
  const {
    activeTenantId,
    configs,
    selectedConfigId,
    onSelectConfigId,
    formSchema,
    selectedSectionIndex,
    onSelectSectionIndex,
    onOpenAddSection,
    onQuickAddFieldType,
  } = props;

  const sections = useMemo(
    () => ((formSchema?.sections as Array<Record<string, unknown>> | undefined) || []).map((s, idx) => ({
      idx,
      id: String(s.id ?? idx),
      title: String((s as { title?: string }).title ?? (s as { name?: string }).name ?? s.id ?? `Section ${idx + 1}`),
    })),
    [formSchema],
  );

  return (
    <Box
      sx={{
        width: { xs: '100%', lg: STUDIO_PANEL.leftWidth },
        flexShrink: 0,
        borderRight: { lg: 1 },
        borderBottom: { xs: 1, lg: 0 },
        borderColor: 'divider',
        bgcolor: (t) => alpha(t.palette.primary.main, 0.03),
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
        <ViewSidebarIcon fontSize="small" color="primary" />
        <Typography variant="subtitle2" fontWeight={700} color="primary">
          Builder
        </Typography>
      </Stack>

      <Stack spacing={1.25} sx={{ flex: 1, minHeight: 0, overflow: 'auto', pr: 0.5 }}>
        <TextField
          select
          size="small"
          label="Config"
          fullWidth
          value={selectedConfigId}
          onChange={(e) => onSelectConfigId(String(e.target.value || ''))}
          disabled={!activeTenantId}
        >
          {configs.map((c) => (
            <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
          ))}
        </TextField>

        <Divider />

        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
          <Stack direction="row" spacing={1} alignItems="center">
            <CategoryOutlinedIcon fontSize="small" color="primary" />
            <Typography variant="caption" color="text.secondary" fontWeight={700}>
              Sections
            </Typography>
          </Stack>
          <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={onOpenAddSection} disabled={!formSchema}>
            New
          </Button>
        </Stack>

        <Stack spacing={0.75}>
          {sections.map((s) => (
            <Button
              key={s.id}
              size="small"
              variant={selectedSectionIndex === s.idx ? 'contained' : 'outlined'}
              onClick={() => onSelectSectionIndex(s.idx)}
              sx={{ justifyContent: 'flex-start' }}
              disabled={!formSchema}
            >
              {s.title}
            </Button>
          ))}
          {!sections.length && (
            <Typography variant="caption" color="text.secondary">
              Add a section to start building your form.
            </Typography>
          )}
        </Stack>

        <Divider />

        <Typography variant="caption" color="text.secondary" fontWeight={700}>
          Fields
        </Typography>
        <Stack direction="row" flexWrap="wrap" gap={1}>
          {PALETTE.map((p) => (
            <Button
              key={p.type}
              size="small"
              variant="outlined"
              onClick={() => onQuickAddFieldType(p.type)}
              disabled={!formSchema || sections.length === 0}
              sx={{ textTransform: 'none' }}
            >
              {p.label}
            </Button>
          ))}
        </Stack>
        <Typography variant="caption" color="text.secondary">
          Tip: click a field type to add it to the active section, then edit its properties on the right.
        </Typography>
      </Stack>
    </Box>
  );
}

